import { Button, Paper, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { CompanySelector } from '../../components/CompanySelector';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { PageHeader } from '../../components/PageHeader';
import { useSelectedCompany } from '../../components/useSelectedCompany';
import { useLanguage } from '../../i18n/LanguageContext';

import { TransferDetail } from './TransferDetail';
import { TransferHistoryTable } from './TransferHistoryTable';
import { TransferStartCards } from './TransferStartCards';

/**
 * Prenos dát (R0037 Q-06): a company's catalogue and stock moved by file — an export to download, an import with its
 * preview and choice, and the history of both. The transfer open is in the address (`?transfer=`), so the mail's link
 * opens it.
 */
export const TransfersPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selection = useSelectedCompany('transfers.manage');
  const company = selection.company;
  const selected = searchParams.get('transfer');
  const select = (transferId: string): void => setSearchParams({ transfer: transferId });

  if (!selection.isLoading && company === null) {
    return (
      <>
        <PageHeader title={t('transfers.title')} />
        <ErrorNotice error={selection.error} onRetry={selection.retry} />
        <Paper variant="outlined">
          <EmptyState
            title={t('products.noCompany')}
            detail={t('transfers.noCompanyDetail')}
            action={<Button onClick={() => navigate('/companies')}>{t('nav.companies')}</Button>}
          />
        </Paper>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('transfers.title')}
        subtitle={t('transfers.subtitle')}
        action={
          <CompanySelector
            companies={selection.companies}
            value={company}
            onChange={(companyId) => {
              selection.select(companyId);
              setSearchParams({});
            }}
          />
        }
      />
      <ErrorNotice error={selection.error} onRetry={selection.retry} />
      {company !== null ? (
        <>
          <TransferStartCards key={`start-${company.id}`} companyId={company.id} onStarted={select} />
          {selected !== null ? (
            <TransferDetail key={selected} companyId={company.id} transferId={selected} />
          ) : null}
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t('transfers.history')}
          </Typography>
          <TransferHistoryTable
            key={company.id}
            companyId={company.id}
            selected={selected}
            onSelect={select}
          />
        </>
      ) : null}
    </>
  );
};
